import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, message, Switch, Card } from 'antd';
import { UserOutlined, LaptopOutlined, HomeOutlined, PoweroffOutlined, AppstoreOutlined, BookOutlined, ApartmentOutlined, AppstoreAddOutlined, AppstoreTwoTone, AlertOutlined, TeamOutlined } from '@ant-design/icons';
import api from './api/axios';
import DeviceList from './components/DeviceList';
import DeviceDetail from './components/DeviceDetail';
import OTAManagement from './components/OTAManagement';
import StudentAppsManagement from './components/StudentAppsManagement';
import ClassAppsManagement from './components/ClassAppsManagement';
import AlarmDataPage from './components/AlarmDataPage';
import Login from './components/Login';
import SchoolLogin from './components/SchoolLogin';
import SchoolDashboard from './components/SchoolDashboard';
import { DEVICE_TYPES } from './utils/deviceTypes';
import './App.css';

const { Header, Content, Sider } = Layout;

function App() {
  const [current, setCurrent] = useState('class-board-devices');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [platform, setPlatform] = useState('admin'); // admin 或 school
  const [schoolTeacher, setSchoolTeacher] = useState(null);
  const [deviceListReturnPage, setDeviceListReturnPage] = useState('class-board-devices');

  const pageDeviceTypeMap = {
    'class-board-devices': DEVICE_TYPES.CLASS_BOARD,
    'class-board-ota': DEVICE_TYPES.CLASS_BOARD,
    'moral-machine-devices': DEVICE_TYPES.MORAL_MACHINE,
    'moral-machine-ota': DEVICE_TYPES.MORAL_MACHINE
  };

  // 优先从URL参数获取平台类型（支持外部跳转）
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const platformParam = urlParams.get('platform');
    
    if (platformParam === 'school' || platformParam === 'admin') {
      setPlatform(platformParam);
    }
  }, []);

  // 检查登录状态
  useEffect(() => {
    // 检查后台管理平台登录
    const adminToken = localStorage.getItem('token');
    const schoolToken = localStorage.getItem('school_token');
    
    if (adminToken) {
      api.get('/auth/me')
      .then(response => {
        setUser(response.data.user);
        setIsLoggedIn(true);
        setPlatform('admin');
      })
      .catch(error => {
        localStorage.removeItem('token');
      });
    } else if (schoolToken) {
      // 检查学校平台登录
      api.get('/school/me')
      .then(response => {
        setSchoolTeacher(response.data.teacher);
        // 更新缓存
        localStorage.setItem('school_teacher', JSON.stringify(response.data.teacher));
        setIsLoggedIn(true);
        setPlatform('school');
      })
      .catch(error => {
        localStorage.removeItem('school_token');
        localStorage.removeItem('school_teacher');
      });
    }
  }, []);

  // 处理后台管理平台登录
  const handleAdminLogin = (userData) => {
    setUser(userData.user);
    localStorage.setItem('token', userData.token);
    setIsLoggedIn(true);
    setPlatform('admin');
    message.success('登录成功');
  };

  // 处理学校管理平台登录
  const handleSchoolLogin = (teacher) => {
    setSchoolTeacher(teacher);
    setIsLoggedIn(true);
    setPlatform('school');
    message.success('登录成功');
  };

  // 处理登出
  const handleLogout = () => {
    if (platform === 'admin') {
      localStorage.removeItem('token');
    } else {
      localStorage.removeItem('school_token');
      localStorage.removeItem('school_teacher');
    }
    setIsLoggedIn(false);
    setUser(null);
    setSchoolTeacher(null);
    setSelectedDevice(null);
    message.success('登出成功');
  };

  // 切换平台
  const handlePlatformSwitch = (value) => {
    setPlatform(value ? 'school' : 'admin');
    setIsLoggedIn(false);
    setUser(null);
    setSchoolTeacher(null);
    setSelectedDevice(null);
    localStorage.removeItem('token');
    localStorage.removeItem('school_token');
    localStorage.removeItem('school_teacher');
  };

  // 处理设备选择
  const handleDeviceSelect = (device) => {
    setSelectedDevice(device);
    setDeviceListReturnPage(current);
    setCurrent('device-detail');
  };

  // 处理返回列表
  const handleBack = () => {
    setCurrent(deviceListReturnPage);
  };

  // 渲染内容
  const renderContent = () => {
    if (!isLoggedIn) {
      if (platform === 'admin') {
        return <Login onLogin={handleAdminLogin} />;
      } else {
        return <SchoolLogin onLogin={handleSchoolLogin} />;
      }
    }

    // 学校管理平台
    if (platform === 'school') {
      return <SchoolDashboard teacher={schoolTeacher} onLogout={handleLogout} />;
    }

    // 后台管理平台
    if (current === 'class-board-devices' || current === 'moral-machine-devices') {
      return (
        <DeviceList
          deviceType={pageDeviceTypeMap[current]}
          onDeviceSelect={handleDeviceSelect}
        />
      );
    }

    if (current === 'device-detail' && selectedDevice) {
      return <DeviceDetail device={selectedDevice} onBack={handleBack} />;
    }

    if (current === 'class-board-ota' || current === 'moral-machine-ota') {
      return <OTAManagement deviceType={pageDeviceTypeMap[current]} />;
    }

    if (current === 'class-board-student-apps') {
      return <StudentAppsManagement />;
    }

    if (current === 'class-board-teacher-apps') {
      return <Card title="教师应用" />;
    }

    if (current === 'class-board-class-apps') {
      return <ClassAppsManagement />;
    }

    if (current === 'class-board-alarms') {
      return <AlarmDataPage scope="admin" />;
    }

    if (current === 'home') {
      return <div>欢迎使用设备管理系统</div>;
    }

    if (current === 'user') {
      return <div>个人中心</div>;
    }

    return <div>请选择功能</div>;
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="logo">
          {platform === 'admin' ? '设备管理系统' : '学校管理平台'}
        </div>
        
        {!isLoggedIn && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ color: '#8c8c8c' }}>
              {platform === 'admin' ? '后台管理平台' : '学校管理平台'}
            </span>
            <Switch
              checked={platform === 'school'}
              onChange={handlePlatformSwitch}
              checkedChildren={<BookOutlined />}
              unCheckedChildren={<ApartmentOutlined />}
            />
          </div>
        )}
        
        {isLoggedIn && platform === 'admin' && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: 16 }}>欢迎, {user?.username}</span>
            <Button type="primary" danger onClick={handleLogout} icon={<PoweroffOutlined />}>
              登出
            </Button>
          </div>
        )}
      </Header>
      
      <Layout>
        {isLoggedIn && platform === 'admin' && (
          <Sider width={200} style={{ background: '#fff' }}>
            <Menu
              mode="inline"
              selectedKeys={[current]}
              defaultOpenKeys={['class-board', 'moral-machine']}
              style={{ height: '100%', borderRight: 0 }}
              onSelect={({ key }) => {
                setCurrent(key);
                if (key !== 'device-detail') {
                  setSelectedDevice(null);
                }
              }}
            >
              <Menu.Item key="home" icon={<HomeOutlined />}>
                首页
              </Menu.Item>
              <Menu.SubMenu key="class-board" icon={<LaptopOutlined />} title="电子班牌">
                <Menu.Item key="class-board-devices" icon={<LaptopOutlined />}>
                  设备列表
                </Menu.Item>
                <Menu.Item key="class-board-ota" icon={<AppstoreOutlined />}>
                  OTA管理
                </Menu.Item>
                <Menu.Item key="class-board-student-apps" icon={<AppstoreAddOutlined />}>
                  学生应用
                </Menu.Item>
                <Menu.Item key="class-board-teacher-apps" icon={<TeamOutlined />}>
                  教师应用
                </Menu.Item>
                <Menu.Item key="class-board-class-apps" icon={<AppstoreTwoTone />}>
                  班级应用
                </Menu.Item>
                <Menu.Item key="class-board-alarms" icon={<AlertOutlined />}>
                  报警数据
                </Menu.Item>
              </Menu.SubMenu>
              <Menu.SubMenu key="moral-machine" icon={<ApartmentOutlined />} title="德育机">
                <Menu.Item key="moral-machine-devices" icon={<LaptopOutlined />}>
                  设备列表
                </Menu.Item>
                <Menu.Item key="moral-machine-ota" icon={<AppstoreOutlined />}>
                  OTA管理
                </Menu.Item>
              </Menu.SubMenu>
              <Menu.Item key="user" icon={<UserOutlined />}>
                个人中心
              </Menu.Item>
            </Menu>
          </Sider>
        )}
        
        {isLoggedIn && platform === 'admin' && (
          <Layout style={{ padding: '24px' }}>
            <Content
              style={{
                background: '#fff',
                padding: 24,
                margin: 0,
                minHeight: 280,
              }}
            >
              {renderContent()}
            </Content>
          </Layout>
        )}
        
        {isLoggedIn && platform === 'school'}
        {isLoggedIn && platform === 'school' && renderContent()}
        
        {!isLoggedIn && (
          <Layout style={{ padding: '24px' }}>
            <Content
              style={{
                background: '#f5f5f5',
                padding: 24,
                margin: 0,
                minHeight: 280,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {renderContent()}
            </Content>
          </Layout>
        )}
      </Layout>
    </Layout>
  );
}

export default App;
